import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { memo, useCallback, useMemo } from 'react';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { GET_LIST_INVITE_AGENT_QUERY_KEY, useGetListInviteAgentQuery } from '../../../api/get-list-invite-agent.api';
import { useResendInviteAgent } from '../../../api/resend-invite-agent.api';
import { TAgentInvitation } from '../../../types/agent.type';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '@/components/_ui/modal';
import { AppTooltip } from '@/components/_ui/app-tooltip/app-tooltip';
import { Mail, SquareX } from 'lucide-react';
import PaginationLimit from '@/components/ui/pagination-limit';
import Pagination from '@/components/ui/pagination';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { useServerPagination } from '@/hooks/use-server-pagination';
import { displayRole } from '@/features/user/utils/check-role';
import TableActionButton from '@/components/ui/refactor/table-action-button';
import type { AxiosErrorCustom } from '@/lib/axios';

export type TPendingInviteAgentModalType = 'PendingInviteAgentModal';
export type IPendingInviteAgentModalProps = Record<string, never>;

// ------------------------------------------------------------------------
const TOAST_MESSAGES_PENDING_INVITE_AGENT = {
  RESEND_INVITATION_ERROR: 'Error resending invitation, please try again',
  RESEND_INVITATION_SUCCESS: 'The invitation has been successfully resent',
};

const PendingInviteAgentModal = memo((_props: IPendingInviteAgentModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenPendingInviteAgentModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('PendingInviteAgentModal');
  }, [userModalRootActions]);

  const { currentPage, itemsPerPage, handlePageChange, handleItemsPerPageChange, apiParams } = useServerPagination({
    initialItemsPerPage: 10,
    initialPage: 1,
  });

  const { data: agentInvitationData, isLoading: isLoadingAgentInvitation } = useGetListInviteAgentQuery({
    params: {
      ...apiParams,
      sortDir: 'ASC',
      sortBy: 'fullname',
    },
    queryConfig: {
      enabled: userModalRootActions.checkIsOpenModal('PendingInviteAgentModal'),
    },
  });

  const currentData = agentInvitationData?.payload?.rows || [];
  const totalItems = agentInvitationData?.payload?.count || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const mutationResendInvitation = useResendInviteAgent({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || TOAST_MESSAGES_PENDING_INVITE_AGENT.RESEND_INVITATION_ERROR;
        showToast({
          type: 'error',
          message: errorMessage,
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INVITE_AGENT_QUERY_KEY] })]);
        showToast({
          type: 'success',
          message: TOAST_MESSAGES_PENDING_INVITE_AGENT.RESEND_INVITATION_SUCCESS,
        });
      },
    },
  });

  const handleResendInvitation = useCallback(
    (invitation: TAgentInvitation) => {
      mutationResendInvitation.mutate({ invitationId: invitation.invitationId });
    },
    [mutationResendInvitation]
  );

  const handleDeleteInvitation = useCallback(
    (invitation: TAgentInvitation) => {
      userModalRootActions.openModal('DeleteInviteAgentModal', {
        invitationId: invitation.invitationId,
      });
    },
    [userModalRootActions]
  );

  const handleClose = useCallback(() => {
    userModalRootActions.closeModal('PendingInviteAgentModal');
  }, [userModalRootActions]);

  return (
    <Modal
      isOpen={isOpenPendingInviteAgentModal}
      lockBodyScroll
      onClose={handleClose}
      className="md:w-[1000px] w-[345px] md:p-[32px] p-[16px]"
    >
      <ModalHeader>
        <ModalTitle>Pending Invitation</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="w-full">
          {isLoadingAgentInvitation ? (
            <PendingInvitationTableSkeleton />
          ) : (
            <>
              <div className="rounded-[8px] rounded-b-none overflow-hidden border border-[#D9D9D9]">
                <table className="w-full text-sm text-left rtl:text-right">
                  <thead className="text-[14px] font-normal bg-[#0C8DBF] text-white">
                    <tr>
                      <th className="w-[60px] text-center py-3">No</th>
                      <th className="w-[200px] px-6 py-3">Name</th>
                      <th className="w-[200px] px-6 py-3">Email</th>
                      <th className="w-[150px] px-6 py-3">Role</th>
                      <th className="w-[200px] px-6 py-3">Teams</th>
                      <th className="w-[150px] px-6 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((invitation, index: number) => (
                      <tr
                        key={index + 1}
                        className={`bg-white ${index !== currentData.length - 1 ? 'border-b border-[#D9D9D9]' : ''}`}
                      >
                        <th className="text-center w-[60px] text-[#25151E] font-normal whitespace-normal break-words">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </th>
                        <td className="w-[200px] px-6 py-3 text-[14px] text-[#25151E] font-normal whitespace-normal break-words">
                          {invitation.fullname}
                        </td>
                        <td className="w-[200px] px-6 py-3 text-[14px] text-[#25151E] font-normal whitespace-normal break-words">
                          {invitation.email}
                        </td>
                        <td className="w-[150px] px-6 py-3 text-[14px] text-[#25151E] font-normal whitespace-normal break-words">
                          {displayRole(invitation.role?.name)}
                        </td>
                        <td className="w-[200px] px-6 py-3 text-[14px] text-[#25151E] font-normal whitespace-normal break-words">
                          {invitation?.teams?.length > 0 ? (
                            <AppTooltip
                              content={
                                <div className="flex flex-wrap gap-2 p-2">
                                  {invitation.teams.map((team, index) => (
                                    <div
                                      key={index}
                                      className="px-3 py-1 bg-others-chat-open text-[#0C8DBF] rounded-full text-sm"
                                    >
                                      {team.name}
                                    </div>
                                  ))}
                                </div>
                              }
                              className="max-w-[300px] border-[#D9D9D9] p-0"
                            >
                              <div className="relative group text-start cursor-pointer">
                                <span className="block text-start">
                                  {invitation.teams
                                    .slice(0, 1)
                                    .map((team) => team.name)
                                    .join(', ')}
                                  {invitation.teams.length > 1 && (
                                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full ml-1">
                                      +{invitation.teams.length - 1}
                                    </span>
                                  )}
                                </span>
                              </div>
                            </AppTooltip>
                          ) : (
                            <span className="">-</span>
                          )}
                        </td>
                        <td className="w-[150px] px-6 py-3">
                          <div className="flex justify-center items-center gap-[10px]">
                            <TableActionButton
                              icon={Mail}
                              variant="neutral"
                              onClick={() => handleResendInvitation?.(invitation)}
                              tooltip="Resend Invitation"
                              tooltipClassName="bg-white text-[#25151E] border max-w-[300px] border-[#D9D9D9] font-normal text-[14px]"
                            />

                            <TableActionButton
                              icon={SquareX}
                              variant="delete"
                              onClick={() => handleDeleteInvitation?.(invitation)}
                              tooltip="Delete Invitation"
                              tooltipClassName="bg-white text-[#25151E] border max-w-[300px] border-[#D9D9D9] font-normal text-[14px]"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center py-3 px-4 space-x-4 bg-white border border-t-0 border-[#D9D9D9] rounded-b-[8px]">
                <div className="text-[14px] font-normal text-[#25151E]">Total {totalItems} entries</div>
                <div className="flex space-x-2">
                  <PaginationLimit value={itemsPerPage} onChange={handleItemsPerPageChange} />
                  <Pagination
                    limit={itemsPerPage}
                    page={currentPage}
                    count={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
});

PendingInviteAgentModal.displayName = 'PendingInviteAgentModal';
export default PendingInviteAgentModal;

// ------------------------------------------------------------------------
// Pending Invitation Table Skeleton

const PendingInvitationTableSkeleton = memo(() => {
  return (
    <div className="mt-[20px]">
      <div className="rounded-[8px] rounded-b-none overflow-hidden border border-[#D9D9D9]">
        <table className="w-full text-sm text-left rtl:text-right">
          <thead className="text-[14px] font-normal bg-[#0C8DBF] text-white">
            <tr>
              <th className="w-[60px] text-center py-3">No</th>
              <th className="w-[200px] px-6 py-3">Name</th>
              <th className="w-[200px] px-6 py-3">Email</th>
              <th className="w-[150px] px-6 py-3">Role</th>
              <th className="w-[200px] px-6 py-3">Teams</th>
              <th className="w-[150px] px-6 py-3 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, index) => (
              <tr key={index} className={`bg-white ${index !== 4 ? 'border-b border-[#D9D9D9]' : ''}`}>
                <th className="text-center w-[60px] text-[#25151E] font-normal whitespace-normal break-words">
                  <div className="h-4 w-8 bg-gray-200 rounded animate-pulse mx-auto" />
                </th>
                <td className="w-[200px] px-6 py-3">
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                </td>
                <td className="w-[200px] px-6 py-3">
                  <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                </td>
                <td className="w-[150px] px-6 py-3">
                  <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                </td>
                <td className="w-[200px] px-6 py-3">
                  <div className="flex gap-1">
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  </div>
                </td>
                <td className="w-[150px] px-6 py-3">
                  <div className="flex justify-center items-center gap-[10px]">
                    <div className="size-6 bg-gray-200 rounded-sm animate-pulse" />
                    <div className="size-6 bg-gray-200 rounded-sm animate-pulse" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Skeleton */}
      <div className="flex justify-between items-center py-3 px-4 space-x-4 bg-white border border-t-0 border-[#D9D9D9] rounded-b-[8px]">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="flex space-x-2">
          <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
});
PendingInvitationTableSkeleton.displayName = 'PendingInvitationTableSkeleton';
